// BetterAuth client pour le frontend Solid.
//
// Expose `authClient` avec :
// - signIn.email / signOut / signUp.email
// - getSession + useSession() (signal réactif Solid)
// - twoFactor.* (plugin TOTP)
// - magicLink.signIn (plugin magic-link)
// - admin.listUsers / createUser / updateUser / removeUser / setUserPassword
//
// Configuration : pointe sur le serveur Express qui mount BetterAuth sur /api/auth.
//
// En natif (Capacitor) : le plugin `bearer` est actif côté serveur.
// Côté client, on injecte `Authorization: Bearer <token>` via onRequest et on
// capte le token de réponse via l'en-tête `set-auth-token` (ou corps data.token).

import { createAuthClient } from "better-auth/solid";
import { adminClient } from "better-auth/client/plugins";
import { twoFactorClient } from "better-auth/client/plugins";
import { magicLinkClient } from "better-auth/client/plugins";
import { apiOrigin, isNativeApp } from "./native";
import { getNativeToken, setNativeToken } from "./native-token";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

function buildNativeFetchOptions() {
  if (!isNativeApp()) return undefined;
  return {
    onRequest: (ctx: { headers: Headers } & AnyRecord) => {
      const token = getNativeToken();
      if (token) {
        ctx.headers.set("Authorization", `Bearer ${token}`);
      }
    },
    onSuccess: (ctx: { response: Response; data: unknown } & AnyRecord) => {
      // Tente d'abord l'en-tête set-auth-token (convention BetterAuth bearer plugin).
      const header = ctx.response.headers.get("set-auth-token");
      if (header) {
        setNativeToken(header);
        return;
      }
      // Fallback : token dans le corps parsé (data.token ou data.data.token).
      const body = ctx.data as AnyRecord | null | undefined;
      const token =
        (typeof body?.token === "string" ? (body.token as string) : undefined) ??
        (typeof (body?.data as AnyRecord | undefined)?.token === "string"
          ? ((body?.data as AnyRecord).token as string)
          : undefined);
      if (token) setNativeToken(token);
    },
  };
}

export const authClient = createAuthClient({
  // apiOrigin() : origine courante sur le web, URL absolue du relay dans
  // l'app native (capacitor://localhost n'est pas une base URL valide pour
  // BetterAuth → écran blanc si on la laisse passer).
  baseURL: apiOrigin(),
  basePath: "/api/auth",
  plugins: [adminClient(), twoFactorClient(), magicLinkClient()],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchOptions: buildNativeFetchOptions() as any,
});

// Helpers typés pour le code RemoteWS legacy qui s'attend à un objet user.
// Renvoyer null si pas de session ou si la requête en cours.
export function getCurrentUser() {
  const session = authClient.useSession();
  const data = session().data;
  return data?.user || null;
}
